import { buildSelectionKey, isSelectableItem } from './selectionKey';

const EMPTY_SELECTION = { keys: [], byKey: {}, lastClickedKey: null, isComplete: false };

const toEntry = (item) => ({
  key: buildSelectionKey(item),
  type: item.type,
  _id: item._id,
  baseProductId: item.baseProductId,
  sku: item.sku,
  title: item.title,
});

/**
 * Pure reducer helper for the products grid's checkbox selection. Range math is done here
 * (against the full loaded `items` array, not the DOM) because the grid is virtualized — a
 * shift-click anchor from hundreds of rows up is very likely unmounted.
 *
 * @param {{ keys: string[], byKey: object, lastClickedKey: string|null }} selection
 * @param {object[]} items - state.products.search.items (all loaded rows, not just visible ones)
 * @param {{ item: object, value: boolean, isRange?: boolean }} action
 * @returns {{ keys: string[], byKey: object, lastClickedKey: string|null }}
 */
export default function applySelection(selection, items, { item, value, isRange }) {
  if (!isSelectableItem(item)) return selection;

  const key = buildSelectionKey(item);

  if (isRange && selection.lastClickedKey) {
    const selectableIndices = [];
    let anchorPos = -1;
    let targetPos = -1;

    items.forEach((candidate) => {
      if (!isSelectableItem(candidate)) return;
      const candidateKey = buildSelectionKey(candidate);
      const pos = selectableIndices.push({ key: candidateKey, item: candidate }) - 1;
      if (candidateKey === selection.lastClickedKey) anchorPos = pos;
      if (candidateKey === key) targetPos = pos;
    });

    // Anchor not found among currently-loaded selectable rows (e.g. filtered out) — fall back
    // to a plain single toggle instead of guessing a range.
    if (anchorPos === -1 || targetPos === -1) {
      return applySelection(selection, items, { item, value, isRange: false });
    }

    const [start, end] = [anchorPos, targetPos].sort((a, b) => a - b);
    const range = selectableIndices.slice(start, end + 1);

    const byKey = { ...selection.byKey };
    const keySet = new Set(selection.keys);

    range.forEach(({ item: rangeItem, key: rangeKey }) => {
      if (value) {
        if (!keySet.has(rangeKey)) {
          keySet.add(rangeKey);
          byKey[rangeKey] = toEntry(rangeItem);
        }
      } else if (keySet.has(rangeKey)) {
        keySet.delete(rangeKey);
        delete byKey[rangeKey];
      }
    });

    return {
      keys: [...keySet], byKey, lastClickedKey: selection.lastClickedKey, isComplete: false,
    };
  }

  const hasKey = Object.prototype.hasOwnProperty.call(selection.byKey, key);

  if (value === hasKey) {
    // idempotent — still move the anchor for a plain click (not for a range click, per above)
    return { ...selection, lastClickedKey: key, isComplete: false };
  }

  if (value) {
    return {
      keys: [...selection.keys, key],
      byKey: { ...selection.byKey, [key]: toEntry(item) },
      lastClickedKey: key,
      isComplete: false,
    };
  }

  const byKey = { ...selection.byKey };
  delete byKey[key];
  return {
    keys: selection.keys.filter((k) => k !== key), byKey, lastClickedKey: key, isComplete: false,
  };
}

export function selectAllLoaded(selection, items) {
  const byKey = { ...selection.byKey };
  const keys = [...selection.keys];

  items.forEach((item) => {
    if (!isSelectableItem(item)) return;
    const key = buildSelectionKey(item);
    if (Object.prototype.hasOwnProperty.call(byKey, key)) return;
    keys.push(key);
    byKey[key] = toEntry(item);
  });

  return { keys, byKey, lastClickedKey: selection.lastClickedKey, isComplete: false };
}

/**
 * Replaces the current selection with the given selectable records (Select All).
 * Listings are dropped. `isComplete` disables Select All — only pass true when every
 * search hit was typed, so dropping a listing is not the same as dropping an unknown id.
 *
 * @param {{ type: string, _id: string }[]} items
 * @param {{ isComplete?: boolean }} [options]
 */
export function materializeSelection(items, { isComplete = true } = {}) {
  const byKey = {};
  const keys = [];

  items.forEach((item) => {
    if (!isSelectableItem(item)) return;
    const key = buildSelectionKey(item);
    keys.push(key);
    byKey[key] = toEntry(item);
  });

  return { keys, byKey, lastClickedKey: null, isComplete };
}

export function deselectAll() {
  return EMPTY_SELECTION;
}

export function pruneSelection(selection, keysToRemove) {
  if (!keysToRemove.length) return selection;
  const removeSet = new Set(keysToRemove);
  if (!selection.keys.some((key) => removeSet.has(key))) return selection;

  const byKey = { ...selection.byKey };
  keysToRemove.forEach((key) => delete byKey[key]);
  return {
    keys: selection.keys.filter((key) => !removeSet.has(key)),
    byKey,
    lastClickedKey: removeSet.has(selection.lastClickedKey) ? null : selection.lastClickedKey,
    isComplete: false,
  };
}

export { EMPTY_SELECTION };
